import {AfterViewInit, Component, DestroyRef, EventEmitter, inject, Input, Output, ViewChild} from '@angular/core';
import {BehaviorSubject, tap} from 'rxjs';
import {AdresseDataService} from '../../../services/adresse-data.service';
import {ActivatedRoute} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup} from '@angular/forms';
import {AdresseModels} from '../../../models/adresse-models';
import {BenutzerModels} from '../../../models/benutzer-models';
import {UserAuthenticationService} from '../../../services/user-authentication.service';
import {MessageBoxService} from '../../../services/message-box.service';
import {MatCheckbox} from '@angular/material/checkbox';
import {DatumFormatPipe} from '../../../pipes/datum-format.pipe';
import {CollapsibleBoxComponent} from '../../../reusable-components/collapsible-box/collapsible-box.component';
import {MatDialog} from '@angular/material/dialog';
import {KontaktEintragComponent} from '../kontakt-eintrag/kontakt-eintrag.component';
import {filter} from 'rxjs/operators';
import Country = AdresseModels.Country;
import AdressStatus = AdresseModels.AdressStatus;
import EmptyDetailAdresseElement = AdresseModels.EmptyDetailAdresseElement;
import AnsprechPartnerListElement = AdresseModels.AnsprechPartnerListElement;
import ObjektListElement = AdresseModels.ObjektListElement;
import AnsprechPartnerElement = AdresseModels.AnsprechPartnerElement;

@Component({
    selector: 'app-address-detail',
    templateUrl: './address-detail.component.html',
    styleUrl: './address-detail.component.scss'
})
export class AddressDetailComponent implements AfterViewInit {
    @Input() currentAddressID$?: BehaviorSubject<string>;
    @Output() selectedObject: EventEmitter<string> = new EventEmitter();
    @ViewChild('name2onAddressLine', {static: true}) name2AddressLineCheckbox!: MatCheckbox;
    @ViewChild('address', {static: true}) address!: CollapsibleBoxComponent;
    @ViewChild('addressObjects', {static: true}) addressObjects!: CollapsibleBoxComponent;
    @ViewChild('addressPartners', {static: true}) addressPartners!: CollapsibleBoxComponent;
    @ViewChild('addressAdditional', {static: true}) addressAdditional!: CollapsibleBoxComponent;

    protected displayedObjListColumns: any = [];
    protected objektListTypeColIndex: number = 1;
    protected addressForm: FormGroup = new FormGroup({
        adressID: new FormControl(''),
        mandator: new FormControl(''),
        shortname: new FormControl(''),

        adressNo: new FormControl({value: '', disabled: true}),
        lastName: new FormControl(''),
        firstName: new FormControl(''),
        letterHead: new FormControl(''),
        salutation: new FormControl(''),
        title: new FormControl(''),
        companyDescription: new FormControl(''),
        street1: new FormControl(''),
        zip: new FormControl(''),
        city: new FormControl(''),
        phone03: new FormControl(''), // mobile
        phone01: new FormControl(''), // tel
        email: new FormControl(''),
        homePage: new FormControl(''),

        street2: new FormControl(''),
        countryISOShort: new FormControl(''),
        country: new FormControl<Country | null>(null),
        adressStatus: new FormControl<AdresseModels.AdressStatus | null>(null),

        lastName2: new FormControl(''),
        firstName2: new FormControl(''),
        title2: new FormControl(''),
        letterHead2: new FormControl(''),
        phone04: new FormControl(''),
        phone02: new FormControl(''),
        email2: new FormControl(''),
        name2First: new FormControl(0),
    });

    protected addressStatus$: BehaviorSubject<AdressStatus[]> = new BehaviorSubject([] as AdressStatus[]);
    protected countries$: BehaviorSubject<Country[]> = new BehaviorSubject([] as Country[]);
    protected dataPartnerAddresses: AnsprechPartnerListElement[] = ([] as AnsprechPartnerListElement[]);
    protected dataObjects: ObjektListElement[] = ([] as ObjektListElement[]);
    protected displayedAnsprechColumns: string[] = ['ha', 'name', 'vorname', 'zuhanden', 'briefanrede', 'telefon', 'natel', 'email', 'pers', 'menuAP',];

    private destroyRef = inject(DestroyRef);
    private objektListFields: AdresseModels.ObjektListField[] = [];
    private updateChanges: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(private route: ActivatedRoute,
                private addressService: AdresseDataService,
                private userAuthService: UserAuthenticationService,
                private messageBoxService: MessageBoxService,
                private _datumsFormatPipe: DatumFormatPipe,
                private dialog: MatDialog) {
        if (!this.userAuthService.getRight(BenutzerModels.UserRightCodes.AdresseMutieren)) {
            this.addressForm.disable();
        }

        this.addressService.getCountries().pipe(
            takeUntilDestroyed(this.destroyRef),
            tap((res: AdresseModels.Country[]) => {
                this.countries$.next(res);
            })
        ).subscribe();

        this.addressService.getAdressStatus().pipe(
            takeUntilDestroyed(this.destroyRef),
            tap((res: AdresseModels.AdressStatus[]) => {
                this.addressStatus$.next(res);
            })
        ).subscribe();

        this.addressForm.valueChanges.pipe(
            takeUntilDestroyed(this.destroyRef),
            filter(() => this.updateChanges.getValue()),
            filter((change) => !!change.adressID),
            tap((change: AdresseModels.DetailAdresseElement) => {
                this.addressService.updateAdresse(change).subscribe();
            })
        ).subscribe();

        this.addressService.getObjectListFelder().then((res) => {
            if (res) {
                this.objektListFields = res;
                let index = -1;
                res.forEach((f: AdresseModels.ObjektListField) => {
                    index += 1;
                    if (f.titel != '') {
                        this.displayedObjListColumns.push(f.titel);
                    } else {
                        this.displayedObjListColumns.push(f.dbField);
                    }

                    if (f.dbField == 'ObjectType') {
                        this.objektListTypeColIndex = index;
                    }
                });
            }
        });
    }

    ngAfterViewInit(): void {
        this.currentAddressID$!.pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(async id => {
                if (id) {
                    this.updateChanges.next(false);
                    await this.loadAddress(id);
                    this.address.open();
                    this.addressAdditional.open();
                    this.address.show();
                    this.updateChanges.next(true);
                } else {
                    this.updateChanges.next(false);
                    this.resetForm();
                    this.updateChanges.next(true);
                }
            })
        ).subscribe();

        this.route.paramMap.pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(url => {
                if (url.get('id')) {
                    this.currentAddressID$?.next(url.get('id')!);
                }
            })
        ).subscribe();
    }

    protected setAnsprechpartner(elem: AdresseModels.AnsprechPartnerListElement): void {
        this.dataPartnerAddresses.forEach((partner) => {
            partner.hauptAnsprechpartner = 0;
            if (partner.adressID == elem.adressID) {
                partner.hauptAnsprechpartner = 1;
            }
        });
        this.addressService.updateAdressPartner(this.dataPartnerAddresses, this.currentAddressID$!.getValue())
            .subscribe({
                next: () => {
                },
                error: (err) => console.error('Der Ansprechpartner konnte nicht aktualisiert werden. ', err),
            });
    }

    protected setAnsprechPersoenlich(elem: AdresseModels.AnsprechPartnerListElement, event: boolean): void {
        this.dataPartnerAddresses.forEach((partner) => {
            if (partner.adressID == elem.adressID) {
                partner.pers = event ? 1 : 0;
            }
        });
        this.addressService.updateAdressPartner(this.dataPartnerAddresses, this.currentAddressID$!.getValue()).subscribe({
            next: () => {
            },
            error: (err) => console.error('Persönlich konnte nicht auf dem Adresspartner gesetzt werden. ', err),
        });
    }

    public async loadPartners(addressID: string): Promise<void> {
        this.dataPartnerAddresses = await this.addressService.getAnsprechpartnerByID(addressID);
    }

    protected editAnsprechpartner(element): void {
        this.dialog.open(KontaktEintragComponent, {
            disableClose: true, width: '800px', data: element, panelClass: 'swissdefault-dialog-container',
        }).afterClosed().subscribe({
            next: (result: AnsprechPartnerElement) => {
                if (result !== undefined) {
                    this.addressService
                        .updateAdressPartner([result], this.currentAddressID$!.getValue())
                        .subscribe({
                            next: (res) => {
                                this.loadPartners(this.currentAddressID$!.getValue());
                            }, error: (err) => console.log(err),
                        });
                }
            }
        });
    }

    protected async delAnsprechpartner(element): Promise<void> {
        if (element.adressID !== '') {
            const value = await this.messageBoxService.askYesNoQuestion('MAIN_APP_DIALOG.DEL_CONTACT.msg', 'MAIN_APP_DIALOG.DEL_CONTACT.title', [element.name])
            if (value) {
                this.addressService.deleteAdressPartner(element.adressID).subscribe({
                    next: () => {
                        this.loadPartners(this.currentAddressID$!.getValue());
                    }, error: (err) => {
                        console.error('Ansprechpartner konnte nicht gelöscht werden: ', err);
                        this.messageBoxService.showError('Der Ansprechpartner konnte nicht gelöscht werden: ' + err, '!! FEHLER !!');
                    },
                });
            }
        }
    }

    protected getObjektListValue(element: AdresseModels.ObjektListElement, dispColumn: string): any {
        let dbFieldObj = this.objektListFields.filter((olf) => olf.titel === dispColumn);
        let dbFieldDesc = '';
        if (dbFieldObj && dbFieldObj.length === 1) {
            dbFieldDesc = dbFieldObj[0].dbField;
        } else {
            dbFieldObj = this.objektListFields.filter((olf) => olf.dbField === dispColumn);
            dbFieldDesc = dbFieldObj[0].dbField;
        }
        return this.readPropertyCaseInsensitive(element, dbFieldDesc);
    }

    protected openObjekt(row: ObjektListElement): void {
        this.selectedObject.emit(row.objectID);
    }

    protected setName2AsFirstAddressLine(checked: boolean): void {
        this.addressForm.patchValue({'name2First': checked ? 1 : 0});
    }

    private readPropertyCaseInsensitive(element: AdresseModels.ObjektListElement, prop: string): any {
        prop = (prop + '').toLowerCase();
        for (let p in element) {
            if (element.hasOwnProperty(p) && prop == (p + '').toLowerCase()) {
                let value = (element as any)[p];

                if (p.toLowerCase() === 'created' || p.toLowerCase() === 'modified') {
                    value = this._datumsFormatPipe.transform(value);
                } else if (p.toLowerCase() === 'typenumber') {
                    switch (value) {
                        case 1:
                            value = 'building_storage.png';
                            break;
                        case 2:
                            value = 'book.png';
                            break;
                        case 3:
                            value = 'folder_open.png';
                            break;
                        case 4:
                            value = 'book_open.png';
                            break;
                        case 5:
                            value = 'supplier.png';
                            break;
                        case 6:
                            value = 'people-worker-filled.png';
                            break;
                        case 7:
                            value = 'people-man-7-filled.png';
                            break;
                        default:
                            value = 'unknown_type.png';
                    }
                }
                return value;
            }
        }
    }

    private async loadAddress(addressID: string): Promise<void> {
        if (addressID) {
            const address = await this.addressService.getAddressByID(addressID);
            // backends send the name of the country as string and expects a Country itself
            // TODO 2026-01-27 https://swissjira.atlassian.net/browse/EN-4394 fix in backend, we get a country name as string while it requires a country object
            if (!address.country['isoShort']) {
                const c = this.countries$.getValue().find(c => c.isoNameNational == (address.country as any));
                if (c) {
                    address.country = c;
                }
            }

            await this.loadPartners(addressID);
            await this.loadObjects(addressID);
            this.addressForm.patchValue(address);
            this.name2AddressLineCheckbox.checked = !!address.name2First;
        }

        if (!this.userAuthService.getRight(BenutzerModels.UserRightCodes.AdresseMutieren)) {
            this.addressForm.disable();
        } else {
            this.addressForm.enable();
        }
    }

    private resetForm(): void {
        this.addressForm.patchValue(EmptyDetailAdresseElement(this.addressService.getDefaultCountry()));
        this.addressForm.disable();
    }

    private async loadObjects(addressID: string): Promise<void> {
        const res = await this.addressService.getObjectList(addressID);
        res.sort((a, b) => {
            const diff = new Date(a.modified ?? a.created).getTime() - new Date(b.modified ?? b.created).getTime();
            if (diff == 0) {
                return 0;
            }
            return diff > 0 ? -1 : 1;
        });
        this.dataObjects = res;
    }
}
